import React, { useState } from "react";
import { Box, styled, Theme, Typography, useMediaQuery } from "@mui/material";
import { useSelectInterval } from "@hooks";
import { InvestmentChart } from "@components";
import { RangeSelector } from "@rentablo/components";
import { STOCK_INDEXES } from "@constants";
import { useChartData } from "./useChartData";

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(6),

  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(6),
  },
}));

type Props = {
  tickerSymbol?: string;
  name: string;
};

export const PerformanceChartContainer = ({ tickerSymbol, name }: Props) => {
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down(430));
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [comparedTickerSymbol, setComparedTickerSymbol] = useState<string>();
  const { range, intervalType, selectInterval, selectCustomInterval } =
    useSelectInterval("oneYear");

  const chartData = useChartData({
    tickerSymbol: tickerSymbol,
    comparedTickerSymbol,
    startDate: range.startDate,
    endDate: range.endDate,
  });

  const handleComparedValueChange = (value: string | null) => {
    setComparedTickerSymbol(value !== null ? value : undefined);
  };

  if (!tickerSymbol || tickerSymbol === "WBX1201") return null;

  return (
    <Box pt={8}>
      <HeaderContainer>
        <Typography variant="subtitle2">Performance-Chart</Typography>
        <RangeSelector
          intervalType={intervalType}
          intervalTypes={[
            "oneWeek",
            "oneMonth",
            "YTD",
            "oneYear",
            "threeYears",
            "fiveYears",
            "sinceBeginning",
            "custom",
          ]}
          compactIntervalTypes={[
            "oneWeek",
            "oneMonth",
            "YTD",
            "threeYears",
            "sinceBeginning",
            "custom",
          ]}
          compact={isXs}
          onSelect={selectInterval}
          onCustomSelect={selectCustomInterval}
          width={isSm ? "100%" : "auto"}
        />
      </HeaderContainer>
      <InvestmentChart
        showEvents
        comparedTicker={comparedTickerSymbol}
        onComparedTickerChange={handleComparedValueChange}
        comparisonValues={STOCK_INDEXES}
        data={chartData}
        customRange={range}
        mainValueLabel={name}
        noDataMessage=""
        showNavigator={false}
        showInCard
      />
    </Box>
  );
};
